import { useCallback, useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Pencil, Trash2 } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import toast from 'react-hot-toast';

import type { NavigationOption } from '@packages/shared-types';
import {
  getNavigationOptions,
  createNavigationOption,
  updateNavigationOption,
  deleteNavigationOption,
  type CreateNavigationOptionPayload,
  type UpdateNavigationOptionPayload,
} from '@/services/navigationOptionService';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { IconSelector } from '@/components/shared/IconSelector';
import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { TableCard } from '@/components/shared/TableCard';
import { TableActionsMenu } from '@/components/shared/TableActionsMenu';

const navigationOptionSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  i18nKey: z.string().min(1, 'Translation key is required'),
  icon: z.string().min(1, 'Icon is required'),
  route: z.enum(['items', 'shelves', 'branches', 'scanners', 'detections']),
});

type NavigationOptionFormValues = z.infer<typeof navigationOptionSchema>;

const ROUTE_OPTIONS = [
  { value: 'items', label: 'Items' },
  { value: 'shelves', label: 'Shelves' },
  { value: 'branches', label: 'Branches' },
  { value: 'scanners', label: 'Scanners' },
  { value: 'detections', label: 'Detections' },
] as const;

export interface NavigationOptionsSectionRef {
  openCreateDialog: () => void;
}

interface NavigationOptionsSectionProps {
  // No props needed - using ref to expose methods
}

export const NavigationOptionsSection = forwardRef<
  NavigationOptionsSectionRef,
  NavigationOptionsSectionProps
>((_props, ref) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<NavigationOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<NavigationOption | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    register: registerCreate,
    handleSubmit: handleSubmitCreate,
    formState: { errors: createErrors, isSubmitting: isCreating },
    reset: resetCreate,
    setValue: setValueCreate,
    watch: watchCreate,
  } = useForm<NavigationOptionFormValues>({
    resolver: zodResolver(navigationOptionSchema),
    defaultValues: {
      name: '',
      i18nKey: '',
      icon: '',
      route: 'items',
    },
  });

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    formState: { errors: editErrors, isSubmitting: isEditing },
    reset: resetEdit,
    setValue: setValueEdit,
    watch: watchEdit,
  } = useForm<NavigationOptionFormValues>({
    resolver: zodResolver(navigationOptionSchema),
    defaultValues: {
      name: '',
      i18nKey: '',
      icon: '',
      route: 'items',
    },
  });

  const loadOptions = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const data = await getNavigationOptions();
      setOptions(data);
    } catch (err) {
      toast.error(t('pages.settings.error.load'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadOptions();
  }, [loadOptions]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openCreateDialog: () => {
      setIsCreateDialogOpen(true);
    },
  }));

  const handleCreate = async (values: NavigationOptionFormValues): Promise<void> => {
    try {
      const payload: CreateNavigationOptionPayload = {
        name: values.name,
        i18nKey: values.i18nKey,
        icon: values.icon,
        route: values.route,
        isDefault: false,
      };
      await createNavigationOption(payload);
      setIsCreateDialogOpen(false);
      resetCreate();
      await loadOptions();
      toast.success(t('pages.settings.actions.save'));
    } catch (err) {
      toast.error(t('pages.settings.error.create'));
    }
  };

  const handleEdit = async (values: NavigationOptionFormValues): Promise<void> => {
    if (!selectedOption) return;
    try {
      const payload: UpdateNavigationOptionPayload = {
        name: values.name,
        i18nKey: values.i18nKey,
        icon: values.icon,
        route: values.route,
      };
      await updateNavigationOption(selectedOption.id, payload);
      setIsEditDialogOpen(false);
      setSelectedOption(null);
      resetEdit();
      await loadOptions();
      toast.success(t('pages.settings.actions.save'));
    } catch (err) {
      toast.error(t('pages.settings.error.update'));
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!selectedOption) return;
    setIsDeleting(true);
    try {
      await deleteNavigationOption(selectedOption.id);
      setIsDeleteDialogOpen(false);
      setSelectedOption(null);
      await loadOptions();
      toast.success(t('pages.settings.actions.delete'));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('pages.settings.error.delete');
      if (errorMessage.includes('default')) {
        toast.error(t('pages.settings.error.deleteDefault'));
      } else {
        toast.error(errorMessage);
      }
      setIsDeleting(false);
    }
  };

  const openEditDialog = useCallback(
    (option: NavigationOption) => {
      setSelectedOption(option);
      resetEdit({
        name: option.name,
        i18nKey: option.i18nKey,
        icon: option.icon,
        route: option.route as NavigationOptionFormValues['route'],
      });
      setIsEditDialogOpen(true);
    },
    [resetEdit],
  );

  const openDeleteDialog = useCallback((option: NavigationOption) => {
    setSelectedOption(option);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleEditClick = useCallback(
    (option: NavigationOption) => {
      return () => openEditDialog(option);
    },
    [openEditDialog],
  );

  const handleDeleteClick = useCallback(
    (option: NavigationOption) => {
      return () => openDeleteDialog(option);
    },
    [openDeleteDialog],
  );

  // Sort options by route, then by name for consistent display
  const sortedOptions = [...options].sort((a, b) => {
    if (a.route !== b.route) {
      return a.route.localeCompare(b.route);
    }
    return a.name.localeCompare(b.name);
  });

  return (
    <>
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('pages.settings.createNavigationOption')}</DialogTitle>
                <DialogDescription>
                  {t('pages.settings.navigationOptionsDescription')}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmitCreate(handleCreate)}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="create-name">
                      {t('pages.settings.optionName')} *
                    </Label>
                    <Input
                      id="create-name"
                      {...registerCreate('name')}
                      placeholder="e.g., Students"
                    />
                    {createErrors.name && (
                      <p className="text-sm text-destructive">
                        {createErrors.name.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-i18nKey">
                      {t('pages.settings.optionI18nKey')} *
                    </Label>
                    <Input
                      id="create-i18nKey"
                      {...registerCreate('i18nKey')}
                      placeholder="e.g., navigation.students"
                    />
                    {createErrors.i18nKey && (
                      <p className="text-sm text-destructive">
                        {createErrors.i18nKey.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-icon">
                      {t('pages.settings.optionIcon')} *
                    </Label>
                    <IconSelector
                      value={watchCreate('icon')}
                      onValueChange={(value) => setValueCreate('icon', value)}
                    />
                    {createErrors.icon && (
                      <p className="text-sm text-destructive">
                        {createErrors.icon.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-route">
                      {t('pages.settings.optionRoute')} *
                    </Label>
                    <Select
                      value={watchCreate('route')}
                      onValueChange={(value) =>
                        setValueCreate('route', value as NavigationOptionFormValues['route'])
                      }
                    >
                      <SelectTrigger id="create-route">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROUTE_OPTIONS.map((route) => (
                          <SelectItem key={route.value} value={route.value}>
                            {t(`pages.settings.route${route.label.charAt(0).toUpperCase() + route.label.slice(1)}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {createErrors.route && (
                      <p className="text-sm text-destructive">
                        {createErrors.route.message}
                      </p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsCreateDialogOpen(false);
                      resetCreate();
                    }}
                  >
                    {t('pages.settings.actions.cancel')}
                  </Button>
                  <Button type="submit" disabled={isCreating}>
                    {isCreating
                      ? t('pages.settings.actions.saving')
                      : t('pages.settings.actions.save')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
      </Dialog>
      <TableCard>
        {isLoading ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <LoadingLogo size="sm" />
            <span className="text-sm text-muted-foreground">{t('pages.tenants.loading')}</span>
          </div>
        ) : sortedOptions.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No navigation options found
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('pages.settings.optionRoute')}</TableHead>
                <TableHead>{t('pages.settings.optionName')}</TableHead>
                <TableHead>{t('pages.settings.optionI18nKey')}</TableHead>
                <TableHead>{t('pages.settings.optionIcon')}</TableHead>
                <TableHead>{t('pages.settings.isDefault')}</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedOptions.map((option) => {
                const IconComponent =
                  (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[option.icon] ||
                  LucideIcons.Boxes;
                return (
                  <TableRow key={option.id}>
                    <TableCell>
                      {t(`pages.settings.route${option.route.charAt(0).toUpperCase() + option.route.slice(1)}`)}
                    </TableCell>
                    <TableCell className="font-medium">{option.name}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {option.i18nKey}
                    </TableCell>
                    <TableCell>
                      <IconComponent className="h-4 w-4" />
                    </TableCell>
                    <TableCell>
                      {option.isDefault ? (
                        <Badge variant="secondary">
                          {t('pages.settings.isDefault')}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <TableActionsMenu
                        actions={[
                          {
                            label: t('pages.settings.actions.edit'),
                            icon: Pencil,
                            onClick: handleEditClick(option),
                          },
                          ...(!option.isDefault
                            ? [
                                {
                                  label: t('pages.settings.actions.delete'),
                                  icon: Trash2,
                                  onClick: handleDeleteClick(option),
                                  variant: 'destructive' as const,
                                },
                              ]
                            : []),
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableCard>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('pages.settings.editNavigationOption')}</DialogTitle>
            <DialogDescription>
              {t('pages.settings.navigationOptionsDescription')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitEdit(handleEdit)}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">
                  {t('pages.settings.optionName')} *
                </Label>
                <Input
                  id="edit-name"
                  {...registerEdit('name')}
                  placeholder="e.g., Students"
                />
                {editErrors.name && (
                  <p className="text-sm text-destructive">
                    {editErrors.name.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-i18nKey">
                  {t('pages.settings.optionI18nKey')} *
                </Label>
                <Input
                  id="edit-i18nKey"
                  {...registerEdit('i18nKey')}
                  placeholder="e.g., navigation.students"
                />
                {editErrors.i18nKey && (
                  <p className="text-sm text-destructive">
                    {editErrors.i18nKey.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-icon">
                  {t('pages.settings.optionIcon')} *
                </Label>
                <IconSelector
                  value={watchEdit('icon')}
                  onValueChange={(value) => setValueEdit('icon', value)}
                />
                {editErrors.icon && (
                  <p className="text-sm text-destructive">
                    {editErrors.icon.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-route">
                  {t('pages.settings.optionRoute')} *
                </Label>
                <Select
                  value={watchEdit('route')}
                  onValueChange={(value) =>
                    setValueEdit('route', value as NavigationOptionFormValues['route'])
                  }
                >
                  <SelectTrigger id="edit-route">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROUTE_OPTIONS.map((route) => (
                      <SelectItem key={route.value} value={route.value}>
                        {t(`pages.settings.route${route.label.charAt(0).toUpperCase() + route.label.slice(1)}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editErrors.route && (
                  <p className="text-sm text-destructive">
                    {editErrors.route.message}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setSelectedOption(null);
                  resetEdit();
                }}
              >
                {t('pages.settings.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isEditing}>
                {isEditing
                  ? t('pages.settings.actions.saving')
                  : t('pages.settings.actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title={t('pages.settings.deleteNavigationOption')}
        description={`Are you sure you want to delete "${selectedOption?.name}"? This action cannot be undone.`}
        confirmText={t('pages.settings.actions.delete')}
        cancelText={t('pages.settings.actions.cancel')}
        variant="destructive"
        isLoading={isDeleting}
        onConfirm={handleDelete}
      />
    </>
  );
});

NavigationOptionsSection.displayName = 'NavigationOptionsSection';
